import { create } from 'zustand';
import { Chat } from '@ai-sdk/react';
import type { ChatOnFinishCallback } from 'ai';
import { DefaultChatTransport } from 'ai';
import type {
  TerraUIMessage,
  CopilotMode,
  CopilotViewContext,
  HostInventory,
  TerminalApprovalSettings,
} from '@terra/shared-dto-chat-types';
import { DEFAULT_COPILOT_MODE } from '@terra/shared-dto-chat-types';
import type { CoPilotTargetType } from '@terra/shared-schemas-flow';

import { useProjectStore } from './projectStore';
import { useLayoutStore } from './layoutStore';
import { mapTabToCopilotViewContext } from '../utils/copilot-view-context';
import { getCachedHostInventory } from '../utils/host-inventory-cache';
import { v4 as uuidv4 } from 'uuid';
import { getSessionToken } from '../utils/getSessionToken';
import { getInitialMessages } from '../components/chat/getInitialMessages';
import { createTenantStorage } from './tenantStorage';
import { z } from 'zod';
import { useSkillDraftsStore, type SkillDraft } from './skillDraftsStore';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { useChatHistoryPaginationStore } from './chatHistoryPaginationStore';
import { useCopilotSettingsRefreshStore } from './copilotSettingsRefreshStore';
import { isTargetType, targetSupports } from '../utils/target-scope.utils';
import {
  ChatStreamPendingError,
  fetchChatStreamResume,
  isChatStreamPendingResponse,
  shouldClearChatStreamResumeFlag,
} from '@terra/shared-utils';
import { consumeBackgroundTurnHandoff } from './background-turn-handoff';
import {
  handleChatInstanceFinish,
  shouldReloadHistoryAfterFinish,
} from './chat-instance-on-finish';
import { settleChatStreamResumeIdleStatus } from './chat-turn-interrupted-store';
import { isRecoverableStreamError } from '../utils/chatNetworkErrors';
import { useChatStreamResumeStore } from './chat-stream-resume-store';
import { getTerminalApprovalSettings } from './terminalApprovalStore';
import {
  takeCachedCopilotPromoteResponse,
  toCopilotInvokeTransportResponse,
  readCopilotRequestMessageIdFromInit,
  readBusyCopilotFollowUps,
} from './copilot-pending-follow-up';

type CopilotInvokeBodyExtras = {
  chatId: string;
  applicationId: string | undefined;
  surfaceId: string | undefined;
  targetType: CoPilotTargetType | undefined;
  securityDomain: string | undefined;
  mode: CopilotMode;
  taskContext: TaskContext | undefined;
  terminalApproval: TerminalApprovalSettings;
  draftContext: SkillDraft | undefined;
  proxyToolsEnabled: boolean;
  applicationSettingsRefresh: boolean;
  view: CopilotViewContext | undefined;
  hostInventory: HostInventory | undefined;
};

function resolveActiveTabViewContext(): CopilotViewContext | undefined {
  const { mainContentTabs, activeMainContentTabId } = useLayoutStore.getState();
  const activeTab =
    (activeMainContentTabId
      ? mainContentTabs.find((tab) => tab.id === activeMainContentTabId)
      : undefined) ?? mainContentTabs.find((tab) => tab.isActive);

  return mapTabToCopilotViewContext(activeTab);
}

export function getCopilotInvokeBodyExtras({
  chatId,
  getConfig,
  consumePendingSettingsRefresh = false,
}: {
  chatId: string;
  getConfig: () => ChatInstanceConfig | undefined;
  consumePendingSettingsRefresh?: boolean;
}): CopilotInvokeBodyExtras {
  const currentConfig = getConfig();

  if (!currentConfig) {
    throw new Error(`Config not found for chatId: ${chatId}`);
  }

  const taskContext = currentConfig.taskContextRef.current;
  const projectTarget = resolveCopilotTargetFields({
    targetId: useProjectStore.getState().targetId || undefined,
    targetType: useProjectStore.getState().targetType,
  });
  const applicationId =
    projectTarget.applicationId ?? currentConfig.applicationId;
  const surfaceId = projectTarget.surfaceId ?? currentConfig.surfaceId;
  const targetType = projectTarget.targetType ?? currentConfig.targetType;
  const securityDomain =
    projectTarget.securityDomain ?? currentConfig.securityDomain;
  const draftContext = currentConfig.skillDraftId
    ? useSkillDraftsStore.getState().getDraft(currentConfig.skillDraftId)
    : undefined;

  return {
    chatId: currentConfig.chatId,
    applicationId: surfaceId ? undefined : applicationId,
    surfaceId,
    targetType,
    securityDomain,
    mode: currentConfig.mode ?? DEFAULT_COPILOT_MODE,
    taskContext,
    terminalApproval: getTerminalApprovalSettings(),
    draftContext,
    proxyToolsEnabled:
      targetType !== undefined &&
      isTargetType(targetType) &&
      targetSupports({ targetType, capability: 'proxyTools' }),
    applicationSettingsRefresh: consumePendingSettingsRefresh
      ? useCopilotSettingsRefreshStore
          .getState()
          .consumeSettingsRefreshPending(chatId)
      : false,
    view: resolveActiveTabViewContext(),
    hostInventory: getCachedHostInventory(),
  };
}

type TaskContext = {
  applicationId: string | null;
  applicationUrl?: string | null;
};

export type ChatInstanceConfig = {
  chatId: string;
  applicationId?: string;
  surfaceId?: string;
  targetType?: CoPilotTargetType;
  securityDomain?: string;
  mode?: CopilotMode;
  conversationId: string | null;
  initialMessages?: TerraUIMessage[];
  getChatExecuteUrl: () => string;
  taskContextRef: React.RefObject<TaskContext | undefined>;
  onFinish?: ChatOnFinishCallback<TerraUIMessage>;
  sendMessage?: (message: TerraUIMessage) => Promise<void>;
  resumeStream?: () => void;
  setMessages?: (messages: TerraUIMessage[]) => void;
  onResumeStreamSettled?: (response: Response) => void | Promise<void>;
  intentionalAbortRef?: React.RefObject<boolean>;
  abortStream?: () => Promise<void>;
  skillDraftId?: string;
};

export function resolveCopilotTargetFields({
  targetId,
  targetType,
}: {
  targetId: string | undefined;
  targetType: string | undefined;
}): Pick<
  ChatInstanceConfig,
  'applicationId' | 'surfaceId' | 'targetType' | 'securityDomain'
> {
  if (targetType === 'EXTERNAL_NETWORK' && targetId) {
    return {
      surfaceId: targetId,
      applicationId: undefined,
      targetType: 'EXTERNAL_NETWORK',
      securityDomain: 'external-network',
    };
  }

  if (targetType === 'INTERNAL_NETWORK' && targetId) {
    return {
      surfaceId: targetId,
      applicationId: undefined,
      targetType: 'INTERNAL_NETWORK',
      securityDomain: 'internal-network',
    };
  }

  if (targetType === 'AI_APPLICATION' && targetId) {
    return {
      applicationId: targetId,
      surfaceId: undefined,
      targetType: 'AI_APPLICATION',
      securityDomain: 'web-app',
    };
  }

  if (!targetId) {
    return {
      applicationId: undefined,
      surfaceId: undefined,
      targetType: undefined,
      securityDomain: undefined,
    };
  }

  return {
    applicationId: targetId,
    surfaceId: undefined,
    targetType: 'WEB_APPLICATION',
    securityDomain: 'web-app',
  };
}

export type QueuedChatMessage = {
  id: string;
  message: TerraUIMessage;
  createdAt: number;
  state: 'queued' | 'sending' | 'editing';
};

type PersistedChatInstancesState = {
  queuedMessages: Array<[string, QueuedChatMessage[]]>;
};

type ChatRuntimeStatus = 'streaming' | 'submitted' | 'ready' | 'error';

interface ChatInstancesState {
  configs: Map<string, ChatInstanceConfig>;
  chats: Map<string, Chat<TerraUIMessage>>;
  chatStatuses: Map<string, ChatRuntimeStatus>;
  queuedMessages: Map<string, QueuedChatMessage[]>;

  // Actions
  getOrCreateChat: (config: ChatInstanceConfig) => Chat<TerraUIMessage>;
  setChatStatus: (chatId: string, status: ChatRuntimeStatus) => void;
  updateConfig: (chatId: string, updates: Partial<ChatInstanceConfig>) => void;
  setChatMode: (chatId: string, mode: CopilotMode) => void;
  getChatMode: (chatId: string) => CopilotMode;
  removeConfig: (chatId: string) => void;
  clearAll: () => void;
  discardInMemoryChats: () => void;
  rehydrateQueuedMessages: () => void;
  getChatStatus: (
    chatId: string,
  ) => 'streaming' | 'submitted' | 'ready' | 'error' | null;
  getQueuedMessages: ({ chatId }: { chatId: string }) => QueuedChatMessage[];
  enqueueMessage: ({
    chatId,
    message,
  }: {
    chatId: string;
    message: TerraUIMessage;
  }) => string;
  dequeueNextMessage: ({
    chatId,
  }: {
    chatId: string;
  }) => QueuedChatMessage | null;
  restoreQueuedMessage: ({
    chatId,
    queuedMessage,
    index,
  }: {
    chatId: string;
    queuedMessage: QueuedChatMessage;
    index?: number;
  }) => void;
  startEditingQueuedMessage: ({
    chatId,
    queueItemId,
  }: {
    chatId: string;
    queueItemId: string;
  }) => QueuedChatMessage | null;
  cancelEditingQueuedMessage: ({
    chatId,
    queueItemId,
  }: {
    chatId: string;
    queueItemId: string;
  }) => void;
  updateQueuedMessage: ({
    chatId,
    queueItemId,
    message,
    state,
  }: {
    chatId: string;
    queueItemId: string;
    message: TerraUIMessage;
    state?: QueuedChatMessage['state'];
  }) => void;
  removeQueuedMessage: ({
    chatId,
    queueItemId,
  }: {
    chatId: string;
    queueItemId: string;
  }) => void;
  reorderQueuedMessages: ({
    chatId,
    fromIndex,
    toIndex,
  }: {
    chatId: string;
    fromIndex: number;
    toIndex: number;
  }) => void;
  /** Send a message to a specific chat instance by chatId */
  sendMessageToChat: (chatId: string, message: TerraUIMessage) => Promise<void>;
}

const CHAT_INSTANCES_STORAGE_KEY = 'terra-proxy-chat-instances-store';

const QueuedChatMessageSchema = z.object({
  id: z.string(),
  message: z.custom<TerraUIMessage>(),
  createdAt: z.number(),
  state: z.enum(['queued', 'sending', 'editing']),
});

const PersistedChatInstancesStateSchema = z.object({
  queuedMessages: z.array(
    z.tuple([z.string(), z.array(QueuedChatMessageSchema)]),
  ),
});

const normalizePersistedQueuedMessage = (
  queueItem: QueuedChatMessage,
): QueuedChatMessage =>
  queueItem.state === 'queued' ? queueItem : { ...queueItem, state: 'queued' };

const getPersistableQueuedMessages = (
  queuedMessages: Map<string, QueuedChatMessage[]>,
): PersistedChatInstancesState => ({
  queuedMessages: Array.from(queuedMessages.entries())
    .map(
      ([chatId, queuedMessagesForChat]) =>
        [
          chatId,
          queuedMessagesForChat.map((queueItem) =>
            normalizePersistedQueuedMessage(queueItem),
          ),
        ] satisfies [string, QueuedChatMessage[]],
    )
    .filter(([, queuedMessagesForChat]) => queuedMessagesForChat.length > 0),
});

const loadPersistedQueuedMessages = (): Map<string, QueuedChatMessage[]> => {
  try {
    const persistedValue = createTenantStorage().getItem(
      CHAT_INSTANCES_STORAGE_KEY,
    );

    if (typeof persistedValue !== 'string') {
      return new Map();
    }

    const parseResult = PersistedChatInstancesStateSchema.safeParse(
      JSON.parse(persistedValue),
    );

    if (!parseResult.success) {
      return new Map();
    }

    return new Map(
      parseResult.data.queuedMessages.map(
        ([chatId, queuedMessagesForChat]) =>
          [
            chatId,
            queuedMessagesForChat.map((queueItem) =>
              normalizePersistedQueuedMessage(queueItem),
            ),
          ] satisfies [string, QueuedChatMessage[]],
      ),
    );
  } catch {
    return new Map();
  }
};

const persistQueuedMessages = (
  queuedMessages: Map<string, QueuedChatMessage[]>,
) => {
  try {
    createTenantStorage().setItem(
      CHAT_INSTANCES_STORAGE_KEY,
      JSON.stringify(getPersistableQueuedMessages(queuedMessages)),
    );
  } catch {
    return;
  }
};

const CHAT_STATUS_SYNC_THROTTLE_MS = 50;
const chatStatusListenerAttachedChatIds = new Set<string>();
const chatStatusListenerUnsubscribers = new Map<string, () => void>();

const sessionCompletionCallbacks = new Set<(chatId: string) => void>();

export const subscribeToSessionCompletion = (
  callback: (chatId: string) => void,
): (() => void) => {
  sessionCompletionCallbacks.add(callback);

  return () => sessionCompletionCallbacks.delete(callback);
};

const detachChatStatusListener = (chatId: string) => {
  chatStatusListenerAttachedChatIds.delete(chatId);
  const unsubscribe = chatStatusListenerUnsubscribers.get(chatId);

  if (unsubscribe) {
    unsubscribe();
    chatStatusListenerUnsubscribers.delete(chatId);
  }
};

const attachChatStatusListener = ({
  chatId,
  chat,
  setChatStatus,
}: {
  chatId: string;
  chat: Chat<TerraUIMessage>;
  setChatStatus: (chatId: string, status: ChatRuntimeStatus) => void;
}) => {
  if (chatStatusListenerAttachedChatIds.has(chatId)) {
    return;
  }

  chatStatusListenerAttachedChatIds.add(chatId);

  const unsubscribe = chat['~registerMessagesCallback'](() => {
    setChatStatus(chatId, chat.status);
  }, CHAT_STATUS_SYNC_THROTTLE_MS);

  chatStatusListenerUnsubscribers.set(chatId, unsubscribe);

  queueMicrotask(() => {
    setChatStatus(chatId, chat.status);
  });
};

export const useChatInstancesStore = create<ChatInstancesState>((set, get) => ({
  configs: new Map(),
  chats: new Map(),
  chatStatuses: new Map(),
  queuedMessages: loadPersistedQueuedMessages(),

  setChatStatus: (chatId, status) => {
    const { chatStatuses } = get();

    if (chatStatuses.get(chatId) === status) {
      return;
    }

    const nextChatStatuses = new Map(chatStatuses);

    nextChatStatuses.set(chatId, status);
    set({ chatStatuses: nextChatStatuses });
  },

  getOrCreateChat: (config: ChatInstanceConfig) => {
    const { chats, configs } = get();
    const { setChatStatus } = get();

    // Return existing chat instance if it exists
    const existingChat = chats.get(config.chatId);

    if (existingChat) {
      if (!configs.has(config.chatId)) {
        configs.set(config.chatId, config);
      }

      queueMicrotask(() => {
        attachChatStatusListener({
          chatId: config.chatId,
          chat: existingChat,
          setChatStatus: get().setChatStatus,
        });
      });

      return existingChat;
    }

    configs.set(config.chatId, config);

    // Create transport for this chat instance
    const transport = new DefaultChatTransport({
      api: config.getChatExecuteUrl(),
      credentials: 'include',
      headers: async () => {
        const headers: Record<string, string> = {
          'X-Chat-ID': config.chatId,
        };

        const isElectron =
          typeof window !== 'undefined' && !!window.electronAPI;

        if (!isElectron) {
          const sessionToken = getSessionToken();

          if (sessionToken) {
            headers['Authorization'] = `Bearer ${sessionToken}`;
          }
        }

        return headers;
      },
      prepareReconnectToStreamRequest: ({ id }) => {
        const reconnectHeaders: Record<string, string> = {
          'X-Chat-ID': config.chatId,
        };

        const isElectron =
          typeof window !== 'undefined' && !!window.electronAPI;

        if (!isElectron) {
          const sessionToken = getSessionToken();

          if (sessionToken) {
            reconnectHeaders['Authorization'] = `Bearer ${sessionToken}`;
          }
        }

        return {
          api: `${getCurrentGatewayUrl()}/chat/graphs/co-pilot/${id}/stream`,
          credentials: 'include',
          headers: reconnectHeaders,
        };
      },
      prepareSendMessagesRequest: ({ id, messages }) => {
        const invokeExtras = getCopilotInvokeBodyExtras({
          chatId: config.chatId,
          getConfig: () => get().configs.get(config.chatId),
          consumePendingSettingsRefresh: true,
        });

        return {
          body: {
            id,
            message: messages[messages.length - 1],
            ...invokeExtras,
          },
        };
      },
      fetch: (async (url: unknown, options?: RequestInit) => {
        if (options?.method === 'GET') {
          const response = await fetchChatStreamResume(String(url), options);

          settleChatStreamResumeIdleStatus({
            chatId: config.chatId,
            response,
            clearError: get().chats.get(config.chatId)?.clearError,
          });
          const onResumeStreamSettled = get().configs.get(
            config.chatId,
          )?.onResumeStreamSettled;

          try {
            await onResumeStreamSettled?.(response);
          } finally {
            if (
              shouldClearChatStreamResumeFlag({ resumeStatus: response.status })
            ) {
              useChatStreamResumeStore
                .getState()
                .setStreamResuming(config.chatId, false);
            }
          }

          if (isChatStreamPendingResponse(response)) {
            throw new ChatStreamPendingError();
          }

          return response;
        }

        const currentConfig = get().configs.get(config.chatId);

        if (!currentConfig) {
          throw new Error(`Config not found for chatId: ${config.chatId}`);
        }

        const requestMessageId = readCopilotRequestMessageIdFromInit(options);
        const cachedPromoteResponse =
          requestMessageId === undefined
            ? undefined
            : takeCachedCopilotPromoteResponse({
                chatId: config.chatId,
                messageId: requestMessageId,
              });

        if (cachedPromoteResponse) {
          return cachedPromoteResponse;
        }

        const actualUrl = currentConfig.getChatExecuteUrl();
        const response = await fetch(actualUrl, options);

        return toCopilotInvokeTransportResponse(response);
      }) as typeof fetch,
      body: () =>
        getCopilotInvokeBodyExtras({
          chatId: config.chatId,
          getConfig: () => get().configs.get(config.chatId),
        }),
    });

    // Create Chat instance with transport
    const chat = new Chat<TerraUIMessage>({
      id: config.chatId,
      generateId: uuidv4,
      transport,
      messages: config.initialMessages ?? [...getInitialMessages()],
      onFinish: (finishEvent) => {
        const currentConfig = get().configs.get(config.chatId);
        const backgroundTurnHandoff = finishEvent.isAbort
          ? consumeBackgroundTurnHandoff(config.chatId)
          : 'none';

        const wasResuming = useChatStreamResumeStore
          .getState()
          .isStreamResuming(config.chatId);

        handleChatInstanceFinish({
          finishEvent,
          intentionalAbort:
            currentConfig?.intentionalAbortRef?.current ?? false,
          isTerminalError:
            finishEvent.isError &&
            !isRecoverableStreamError(get().chats.get(config.chatId)?.error),
          onResumedStreamSettled: shouldReloadHistoryAfterFinish({
            finishEvent,
            wasResuming,
            hasQueuedFollowUps:
              readBusyCopilotFollowUps(config.chatId).followUps.length > 0,
          })
            ? () => {
                void currentConfig?.onResumeStreamSettled?.(
                  new Response(null, { status: 204 }),
                );
              }
            : undefined,
          backgroundTurnHandoff: backgroundTurnHandoff !== 'none',
          backgroundTurnResumeStarted:
            backgroundTurnHandoff === 'resume-started',
          resumeStream: currentConfig?.resumeStream,
          setChatStatus: (status) => setChatStatus(config.chatId, status),
          onSessionComplete: () => {
            sessionCompletionCallbacks.forEach((cb) => cb(config.chatId));
          },
          onFinish: currentConfig?.onFinish ?? config.onFinish,
          setStreamResuming: (isResuming) => {
            useChatStreamResumeStore
              .getState()
              .setStreamResuming(config.chatId, isResuming);
          },
          messages: get().chats.get(config.chatId)?.messages,
          setMessages: currentConfig?.setMessages,
        });
      },
    });

    chats.set(config.chatId, chat);
    queueMicrotask(() => {
      attachChatStatusListener({
        chatId: config.chatId,
        chat,
        setChatStatus: get().setChatStatus,
      });
    });

    return chat;
  },

  getChatStatus: (chatId: string): ChatRuntimeStatus | null => {
    const { chatStatuses, chats } = get();

    return chatStatuses.get(chatId) ?? chats.get(chatId)?.status ?? null;
  },

  getQueuedMessages: ({ chatId }: { chatId: string }) => {
    return get().queuedMessages.get(chatId) ?? [];
  },

  enqueueMessage: ({
    chatId,
    message,
  }: {
    chatId: string;
    message: TerraUIMessage;
  }) => {
    const queueItemId = uuidv4();
    const { queuedMessages } = get();
    const existingQueue = queuedMessages.get(chatId) ?? [];
    const updatedQueue = [
      ...existingQueue,
      {
        id: queueItemId,
        message,
        createdAt: Date.now(),
        state: 'queued' as const,
      },
    ];

    queuedMessages.set(chatId, updatedQueue);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({ queuedMessages: updatedQueuedMessages });

    return queueItemId;
  },

  dequeueNextMessage: ({ chatId }: { chatId: string }) => {
    const { queuedMessages } = get();
    const existingQueue = queuedMessages.get(chatId) ?? [];

    if (existingQueue.length === 0) {
      return null;
    }

    if (
      existingQueue[0]?.state !== 'queued' ||
      existingQueue.some((queueItem) => queueItem.state === 'editing')
    ) {
      return null;
    }

    const [nextMessage, ...remainingQueue] = existingQueue;

    queuedMessages.set(chatId, remainingQueue);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({ queuedMessages: updatedQueuedMessages });

    return nextMessage;
  },

  restoreQueuedMessage: ({
    chatId,
    queuedMessage,
    index = 0,
  }: {
    chatId: string;
    queuedMessage: QueuedChatMessage;
    index?: number;
  }) => {
    const { queuedMessages } = get();
    const existingQueue = queuedMessages.get(chatId) ?? [];

    if (existingQueue.some((queueItem) => queueItem.id === queuedMessage.id)) {
      return;
    }

    const restoredQueueItem = {
      ...queuedMessage,
      state: 'queued' as const,
    };
    const insertIndex = Math.min(Math.max(index, 0), existingQueue.length);
    const updatedQueue = [...existingQueue];

    updatedQueue.splice(insertIndex, 0, restoredQueueItem);
    queuedMessages.set(chatId, updatedQueue);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({ queuedMessages: updatedQueuedMessages });
  },

  startEditingQueuedMessage: ({
    chatId,
    queueItemId,
  }: {
    chatId: string;
    queueItemId: string;
  }) => {
    const { queuedMessages } = get();
    const existingQueue = queuedMessages.get(chatId) ?? [];
    const queuedMessage = existingQueue.find(
      (queueItem) => queueItem.id === queueItemId,
    );

    if (!queuedMessage) {
      return null;
    }

    const updatedQueue = existingQueue.map((queueItem) => {
      if (queueItem.id === queueItemId) {
        return { ...queueItem, state: 'editing' as const };
      }

      if (queueItem.state === 'editing') {
        return { ...queueItem, state: 'queued' as const };
      }

      return queueItem;
    });

    queuedMessages.set(chatId, updatedQueue);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({ queuedMessages: updatedQueuedMessages });

    return (
      updatedQueue.find((queueItem) => queueItem.id === queueItemId) ?? null
    );
  },

  cancelEditingQueuedMessage: ({
    chatId,
    queueItemId,
  }: {
    chatId: string;
    queueItemId: string;
  }) => {
    const { queuedMessages } = get();
    const existingQueue = queuedMessages.get(chatId) ?? [];
    let didUpdate = false;
    const updatedQueue = existingQueue.map((queueItem) => {
      if (queueItem.id === queueItemId && queueItem.state === 'editing') {
        didUpdate = true;

        return { ...queueItem, state: 'queued' as const };
      }

      return queueItem;
    });

    if (!didUpdate) {
      return;
    }

    queuedMessages.set(chatId, updatedQueue);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({ queuedMessages: updatedQueuedMessages });
  },

  updateQueuedMessage: ({
    chatId,
    queueItemId,
    message,
    state = 'queued',
  }: {
    chatId: string;
    queueItemId: string;
    message: TerraUIMessage;
    state?: QueuedChatMessage['state'];
  }) => {
    const { queuedMessages } = get();
    const existingQueue = queuedMessages.get(chatId) ?? [];
    const updatedQueue = existingQueue.map((queueItem) =>
      queueItem.id === queueItemId
        ? {
            ...queueItem,
            message,
            state,
          }
        : queueItem,
    );

    queuedMessages.set(chatId, updatedQueue);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({ queuedMessages: updatedQueuedMessages });
  },

  removeQueuedMessage: ({
    chatId,
    queueItemId,
  }: {
    chatId: string;
    queueItemId: string;
  }) => {
    const { queuedMessages } = get();
    const existingQueue = queuedMessages.get(chatId) ?? [];
    const updatedQueue = existingQueue.filter(
      (queueItem) => queueItem.id !== queueItemId,
    );

    queuedMessages.set(chatId, updatedQueue);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({ queuedMessages: updatedQueuedMessages });
  },

  reorderQueuedMessages: ({
    chatId,
    fromIndex,
    toIndex,
  }: {
    chatId: string;
    fromIndex: number;
    toIndex: number;
  }) => {
    const { queuedMessages } = get();
    const existingQueue = queuedMessages.get(chatId) ?? [];

    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= existingQueue.length ||
      toIndex >= existingQueue.length ||
      fromIndex === toIndex
    ) {
      return;
    }

    const updatedQueue = [...existingQueue];
    const [movedMessage] = updatedQueue.splice(fromIndex, 1);

    if (!movedMessage) {
      return;
    }

    updatedQueue.splice(toIndex, 0, movedMessage);
    queuedMessages.set(chatId, updatedQueue);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({ queuedMessages: updatedQueuedMessages });
  },

  updateConfig: (chatId: string, updates: Partial<ChatInstanceConfig>) => {
    const { configs } = get();
    const existingConfig = configs.get(chatId);

    if (existingConfig) {
      const hasChanges =
        existingConfig.applicationId !== updates.applicationId ||
        existingConfig.surfaceId !== updates.surfaceId ||
        existingConfig.targetType !== updates.targetType ||
        existingConfig.securityDomain !== updates.securityDomain ||
        (updates.mode !== undefined && existingConfig.mode !== updates.mode) ||
        existingConfig.conversationId !== updates.conversationId ||
        existingConfig.chatId !== updates.chatId ||
        existingConfig.skillDraftId !== updates.skillDraftId ||
        updates.sendMessage !== undefined ||
        updates.resumeStream !== undefined ||
        updates.setMessages !== undefined ||
        updates.onResumeStreamSettled !== undefined ||
        updates.intentionalAbortRef !== undefined ||
        updates.abortStream !== undefined;

      if (hasChanges) {
        configs.set(chatId, { ...existingConfig, ...updates });
        set({ configs: new Map(configs) });
      }
    } else {
      // Config doesn't exist yet, just set it (will be created by getOrCreateChat)
      configs.set(chatId, updates as ChatInstanceConfig);
      set({ configs: new Map(configs) });
    }
  },

  setChatMode: (chatId: string, mode: CopilotMode) => {
    const { configs } = get();
    const existingConfig = configs.get(chatId);

    if (!existingConfig || existingConfig.mode === mode) {
      return;
    }

    configs.set(chatId, { ...existingConfig, mode });
    set({ configs: new Map(configs) });
  },

  getChatMode: (chatId: string) => {
    return get().configs.get(chatId)?.mode ?? DEFAULT_COPILOT_MODE;
  },

  removeConfig: (chatId: string) => {
    const { configs, chats, queuedMessages, chatStatuses } = get();

    detachChatStatusListener(chatId);
    configs.delete(chatId);
    chats.delete(chatId);
    queuedMessages.delete(chatId);
    const nextChatStatuses = new Map(chatStatuses);

    nextChatStatuses.delete(chatId);
    useChatHistoryPaginationStore.getState().clearHistoryPagination(chatId);
    const updatedQueuedMessages = new Map(queuedMessages);

    persistQueuedMessages(updatedQueuedMessages);
    set({
      configs: new Map(configs),
      chats: new Map(chats),
      chatStatuses: nextChatStatuses,
      queuedMessages: updatedQueuedMessages,
    });
  },

  clearAll: () => {
    const queuedMessages = new Map<string, QueuedChatMessage[]>();

    persistQueuedMessages(queuedMessages);
    for (const chatId of chatStatusListenerAttachedChatIds) {
      detachChatStatusListener(chatId);
    }

    useChatHistoryPaginationStore.getState().clearAllHistoryPagination();
    set({
      configs: new Map(),
      chats: new Map(),
      chatStatuses: new Map(),
      queuedMessages,
    });
  },

  discardInMemoryChats: () => {
    for (const chatId of chatStatusListenerAttachedChatIds) {
      detachChatStatusListener(chatId);
    }

    useChatHistoryPaginationStore.getState().clearAllHistoryPagination();
    set({
      configs: new Map(),
      chats: new Map(),
      chatStatuses: new Map(),
    });
  },

  rehydrateQueuedMessages: () => {
    set({ queuedMessages: loadPersistedQueuedMessages() });
  },

  sendMessageToChat: async (chatId: string, message: TerraUIMessage) => {
    const config = get().configs.get(chatId);

    if (config?.sendMessage) {
      await config.sendMessage(message);
    } else {
      console.warn(
        `[ChatInstancesStore] Cannot send message to chat ${chatId}: sendMessage callback not registered`,
      );
    }
  },
}));
